const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const LOOP_TOKEN = /(?:^|[\s_.-])(?:loop|groove|beat)(?:[\s_.-]|$)/i;

export const bpmFromFilename = (name = '') => {
  const text = String(name);
  const explicit = text.match(/(?:^|[^0-9])(\d{2,3}(?:\.\d+)?)\s*(?:bpm)(?:[^a-z]|$)/i);
  if (explicit) {
    const bpm = Number(explicit[1]);
    return bpm >= 40 && bpm <= 260 ? bpm : null;
  }

  // Libraries often use "Drum_Loop_124.wav" without the BPM suffix. Only
  // trust a bare number when the name explicitly identifies loop material.
  if (LOOP_TOKEN.test(text)) {
    const tokens = [...text.matchAll(/(?:^|[^0-9])(\d{2,3}(?:\.\d+)?)(?=[^0-9]|$)/g)];
    const plausible = tokens.map((match) => Number(match[1])).filter((value) => value >= 40 && value <= 260);
    return plausible.length ? plausible[plausible.length - 1] : null;
  }
  return null;
};

const exactBufferDuration = (audioBuffer) => {
  const length = Number(audioBuffer?.length);
  const sampleRate = Number(audioBuffer?.sampleRate);
  if (Number.isFinite(length) && length > 0 && Number.isFinite(sampleRate) && sampleRate > 0) {
    return length / sampleRate;
  }
  return Number(audioBuffer?.duration) || 0;
};

// A combined amplitude/high-frequency novelty envelope is much more stable
// than plain RMS for bass, percussion, and sustained melodic loops alike.
const noveltyEnvelope = (audioBuffer, frameSize = 1024, hopSize = 256) => {
  const channels = Math.max(1, Number(audioBuffer?.numberOfChannels) || 1);
  const length = Math.max(0, Number(audioBuffer?.length) || 0);
  if (length < frameSize) return { envelope: new Float32Array(0), activity: 0 };
  const frames = Math.floor((length - frameSize) / hopSize) + 1;
  const rms = new Float32Array(frames);
  const highFrequency = new Float32Array(frames);
  let signalPeak = 0;

  for (let frame = 0; frame < frames; frame += 1) {
    const start = frame * hopSize;
    let sumSquares = 0;
    let differenceSquares = 0;
    for (let channel = 0; channel < channels; channel += 1) {
      const data = audioBuffer.getChannelData(channel);
      let previous = data[start] || 0;
      for (let i = 0; i < frameSize; i += 1) {
        const sample = data[start + i] || 0;
        sumSquares += sample * sample;
        const difference = sample - previous;
        differenceSquares += difference * difference;
        previous = sample;
      }
    }
    const divisor = frameSize * channels;
    rms[frame] = Math.sqrt(sumSquares / divisor);
    highFrequency[frame] = Math.sqrt(differenceSquares / divisor);
    signalPeak = Math.max(signalPeak, rms[frame]);
  }

  const envelope = new Float32Array(frames);
  let envelopePeak = 0;
  let envelopeSum = 0;
  for (let i = 1; i < frames; i += 1) {
    const from = Math.max(0, i - 12);
    let localRms = 0;
    let localHigh = 0;
    for (let j = from; j < i; j += 1) {
      localRms += rms[j];
      localHigh += highFrequency[j];
    }
    const count = Math.max(1, i - from);
    localRms /= count;
    localHigh /= count;
    const amplitudeFlux = Math.max(0, rms[i] - (localRms * 0.94));
    const highFlux = Math.max(0, highFrequency[i] - (localHigh * 0.9));
    const value = amplitudeFlux + (highFlux * 0.7);
    envelope[i] = value;
    envelopePeak = Math.max(envelopePeak, value);
    envelopeSum += value;
  }

  if (envelopePeak > 1e-9) {
    // Compress dominant kicks so quieter hats/snares still contribute to the
    // periodicity estimate, then remove a small noise floor.
    for (let i = 0; i < envelope.length; i += 1) {
      envelope[i] = Math.max(0, Math.sqrt(envelope[i] / envelopePeak) - 0.04);
    }
  }
  return {
    envelope,
    activity: signalPeak > 1e-7 ? clamp(envelopeSum / (frames * envelopePeak + 1e-9), 0, 1) : 0
  };
};

const correlationAtLag = (envelope, lagValue) => {
  const lag = Math.max(1, Math.round(lagValue));
  if (lag >= envelope.length - 2) return 0;
  let sum = 0;
  let energyA = 0;
  let energyB = 0;
  for (let i = lag; i < envelope.length; i += 1) {
    const a = envelope[i];
    const b = envelope[i - lag];
    sum += a * b;
    energyA += a * a;
    energyB += b * b;
  }
  return clamp(sum / (Math.sqrt(energyA * energyB) + 1e-9), 0, 1);
};

const beatGridScore = (envelope, framesPerBeat) => {
  if (!(framesPerBeat > 1) || envelope.length < framesPerBeat * 2) return 0;
  const phases = 24;
  const tolerance = Math.max(1, framesPerBeat * 0.14);
  let total = 0;
  for (let i = 0; i < envelope.length; i += 1) total += envelope[i];
  if (total < 1e-7) return 0;
  let best = 0;
  for (let phaseIndex = 0; phaseIndex < phases; phaseIndex += 1) {
    const phase = (phaseIndex / phases) * framesPerBeat;
    let aligned = 0;
    for (let i = 0; i < envelope.length; i += 1) {
      const wrapped = ((i - phase) % framesPerBeat + framesPerBeat) % framesPerBeat;
      const distance = Math.min(wrapped, framesPerBeat - wrapped);
      if (distance <= tolerance) aligned += envelope[i] * (1 - (distance / (tolerance + 1e-9)));
    }
    best = Math.max(best, aligned / total);
  }
  return clamp(best, 0, 1);
};

const tempoPrior = (bpm) => {
  // A gentle octave prior only breaks genuine half/double-time ties. It is
  // deliberately too weak to overpower rhythmic evidence or filename data.
  const octavesFromCenter = Math.abs(Math.log2(Math.max(1, bpm) / 120));
  return Math.exp(-octavesFromCenter * octavesFromCenter * 2.2);
};

const candidateBeatCounts = (duration, minBpm, maxBpm) => {
  const minBeats = Math.max(4, Math.ceil((duration * minBpm / 60) / 4) * 4);
  const maxBeats = Math.max(minBeats, Math.floor((duration * maxBpm / 60) / 4) * 4);
  const values = [];
  for (let beats = minBeats; beats <= maxBeats; beats += 4) values.push(beats);
  return values;
};

export function detectLoopBpm(audioBuffer, fileName = '', options = {}) {
  const duration = exactBufferDuration(audioBuffer);
  const sampleRate = Number(audioBuffer?.sampleRate) || 0;
  if (duration < 1 || sampleRate <= 0 || !audioBuffer?.getChannelData) return null;

  const minBpm = clamp(Number(options.minBpm) || 60, 40, 220);
  const maxBpm = clamp(Number(options.maxBpm) || 200, minBpm + 1, 260);
  const hintedBpm = bpmFromFilename(fileName);
  const filenameSaysLoop = LOOP_TOKEN.test(fileName);
  const hopSize = 256;
  const { envelope, activity } = noveltyEnvelope(audioBuffer, 1024, hopSize);
  if (envelope.length < 16 || activity < 0.001) return null;

  let best = null;
  let runnerUp = null;
  candidateBeatCounts(duration, minBpm, maxBpm).forEach((beats) => {
    // This is intentionally not rounded: it is the only BPM that makes this
    // exact decoded sample length occupy exactly `beats` musical beats.
    const bpm = (beats * 60) / duration;
    const framesPerBeat = (sampleRate * 60) / (hopSize * bpm);
    const beatCorrelation = correlationAtLag(envelope, framesPerBeat);
    const twoBeatCorrelation = correlationAtLag(envelope, framesPerBeat * 2);
    const barCorrelation = correlationAtLag(envelope, framesPerBeat * 4);
    const grid = beatGridScore(envelope, framesPerBeat);
    const periodicity = (beatCorrelation * 0.55) + (twoBeatCorrelation * 0.28) + (barCorrelation * 0.17);
    const hint = hintedBpm
      ? Math.exp(-Math.abs(Math.log2(bpm / hintedBpm)) * 12)
      : 0;
    const score = (periodicity * 0.53) + (grid * 0.24) + (tempoPrior(bpm) * 0.1) + (hint * 0.5);
    const candidate = { bpm, beats, score, periodicity, grid, hint };
    if (!best || score > best.score) {
      runnerUp = best;
      best = candidate;
    } else if (!runnerUp || score > runnerUp.score) {
      runnerUp = candidate;
    }
  });
  if (!best) return null;

  const margin = runnerUp ? Math.max(0, best.score - runnerUp.score) : best.score;
  const confidence = clamp(
    (best.periodicity * 0.48)
      + (best.grid * 0.27)
      + (Math.min(0.25, margin) * 0.8)
      + (hintedBpm ? 0.28 : 0)
      + (filenameSaysLoop ? 0.08 : 0),
    0,
    1
  );
  const isLoop = best.beats >= 4
    && best.beats % 4 === 0
    && (confidence >= 0.38 || Boolean(hintedBpm));
  if (!isLoop) return null;

  return {
    bpm: best.bpm,
    beats: best.beats,
    confidence,
    method: hintedBpm ? 'sample-locked+filename+rhythm' : 'sample-locked+rhythm',
    durationSeconds: duration,
    alignmentErrorSeconds: Math.abs(duration - ((best.beats * 60) / best.bpm))
  };
}
